import { SERVER_URL } from '../DB_Url';

const AUTH_EMAIL_TIMEOUT_MS = 10000;
const FALLBACK_ELIGIBLE_STATUS_CODES = new Set([404, 405]);

class AuthEmailApiError extends Error {
  constructor(message, { status = null, shouldFallback = false, data = null, cause = null } = {}) {
    super(message);
    this.name = 'AuthEmailApiError';
    this.status = status;
    this.shouldFallback = shouldFallback;
    this.data = data;
    this.cause = cause;
  }
}

const parseJsonSafely = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return null;
  }

  try {
    return await response.json();
  } catch {
    return null;
  }
};

class AuthEmailApi {
  constructor() {
    this.baseUrl = SERVER_URL;
  }

  isFallbackEligibleStatus(status) {
    return FALLBACK_ELIGIBLE_STATUS_CODES.has(status);
  }

  async request(path, { method = 'POST', body, headers = {} } = {}) {
    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(() => controller.abort(), AUTH_EMAIL_TIMEOUT_MS);

    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      const data = await parseJsonSafely(response);

      if (!response.ok) {
        throw new AuthEmailApiError(
          data?.error || `Auth email request failed with status ${response.status}.`,
          {
            status: response.status,
            shouldFallback: this.isFallbackEligibleStatus(response.status),
            data,
          },
        );
      }

      return data || {};
    } catch (error) {
      if (error instanceof AuthEmailApiError) {
        throw error;
      }

      if (error?.name === 'AbortError') {
        throw new AuthEmailApiError('Auth email request timed out.', {
          shouldFallback: true,
          cause: error,
        });
      }

      throw new AuthEmailApiError(
        error?.message || 'Unable to reach auth email service.',
        {
          shouldFallback: true,
          cause: error,
        },
      );
    } finally {
      globalThis.clearTimeout(timeoutId);
    }
  }

  sendVerificationEmail({ idToken, ...payload }) {
    return this.request('/api/auth/email/verify', {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      body: payload,
    });
  }

  sendPasswordResetEmail(payload) {
    return this.request('/api/auth/email/password-reset', {
      body: payload,
    });
  }
}

const authEmailApi = new AuthEmailApi();

export default authEmailApi;